import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, useJsApiLoader, Circle, Marker } from '@react-google-maps/api';
import { X, Bell, CheckCircle2, MessageSquare, ClipboardList, Users, Loader2, Target, AlertCircle } from 'lucide-react';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import socketService from '@/services/socketService';
import { GOOGLE_MAPS_LOADER_OPTIONS } from './map/MapGisTools';
import { useMapsUnavailable, MapUnavailable } from './map/MapUnavailable';
import {
  GEO_FENCE_LOCATION_FRESHNESS_MS,
  geoFenceLocationState,
  getOrCreateGeoFenceDispatchId,
  isMappableGeoFenceLocation,
  mergeGeoFenceLocationRows,
  newestGeoFenceLocations,
} from './geoFenceActions';

const distKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

const userLabel = (u) => u?.name || u?.full_name || u?.email || 'User';
const userId = (u) => String(u?.id || u?._id || u?.user_id || '').trim();

const locationResponseRows = (response) => {
  if (Array.isArray(response)) return response;
  if (response?.status === false) {
    throw new Error(response?.message || response?.detail || 'Live locations could not be loaded');
  }
  if (Array.isArray(response?.data)) return response.data;
  throw new Error('Live location service returned an invalid response');
};

// Geo-Fencing: draw a circle over responders' latest known positions, and every user
// inside it becomes the target of a notification, verification, status update
// or action card — exactly as if the admin had picked each of them by hand.
// The admin chooses the pool: ALL app users, or only those involved in this
// incident — the map markers and the "inside" counter respect that choice.
const GeoFenceModal = ({ incident, involvedUserIds = [], allowedUserIds = null, onClose, onAction }) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapsUnavailable = useMapsUnavailable();

  const [locationHistory, setLocationHistory] = useState([]); // preserve endpoint history; derive current rows below
  const [users, setUsers] = useState([]);               // app users (names)
  const [pool, setPool] = useState('all');              // 'all' | 'involved'
  const [center, setCenter] = useState(null);           // circle centre (click to set)
  const [radiusKm, setRadiusKm] = useState(2);
  const [sending, setSending] = useState(null);
  const [loadingLiveData, setLoadingLiveData] = useState(true);
  const [locationError, setLocationError] = useState('');
  const [directoryError, setDirectoryError] = useState('');
  const [authoritativeInvolvedUserIds, setAuthoritativeInvolvedUserIds] = useState(null);
  const [freshnessNow, setFreshnessNow] = useState(() => Date.now());
  const actionDispatchIdsRef = useRef({});
  const mapRef = useRef(null);

  const effectiveInvolvedUserIds = authoritativeInvolvedUserIds ?? involvedUserIds;
  const involvedSet = useMemo(
    () => new Set((effectiveInvolvedUserIds || []).map(String)),
    [effectiveInvolvedUserIds]
  );
  const allowedSet = useMemo(() => Array.isArray(allowedUserIds) ? new Set(allowedUserIds.map(String)) : null, [allowedUserIds]);
  const scopedUsers = useMemo(
    () => allowedSet ? users.filter((user) => allowedSet.has(userId(user))) : users,
    [allowedSet, users]
  );
  const scopedUserIdSet = useMemo(
    () => new Set(scopedUsers.map(userId).filter(Boolean)),
    [scopedUsers]
  );
  const allLocations = useMemo(() => newestGeoFenceLocations(locationHistory), [locationHistory]);
  const allowedLatestLocations = useMemo(
    () => allLocations.filter((location) => scopedUserIdSet.has(String(location.user_id))),
    [allLocations, scopedUserIdSet]
  );
  const mappableLocations = useMemo(
    () => allowedLatestLocations.filter(isMappableGeoFenceLocation),
    [allowedLatestLocations]
  );
  const locations = useMemo(
    () => (pool === 'involved' ? mappableLocations.filter((l) => involvedSet.has(String(l.user_id))) : mappableLocations),
    [pool, mappableLocations, involvedSet]
  );
  const involvedMappedCount = useMemo(
    () => mappableLocations.filter((location) => involvedSet.has(String(location.user_id))).length,
    [involvedSet, mappableLocations]
  );
  const involvedUserCount = useMemo(
    () => scopedUsers.filter((user) => involvedSet.has(userId(user))).length,
    [involvedSet, scopedUsers]
  );
  const unmappedUserCount = Math.max(0, scopedUsers.length - mappableLocations.length);
  const liveDataError = directoryError || locationError;

  const refreshLocations = useCallback(async ({ silent = false } = {}) => {
    if (!incident?.id) return;
    if (!silent) setLoadingLiveData(true);
    try {
      const response = await CommanServices.getTeamLocations(incident.id);
      const responseRows = locationResponseRows(response);
      setLocationHistory((previous) => mergeGeoFenceLocationRows(previous, responseRows));
      setFreshnessNow(Date.now());
      setLocationError('');
    } catch (error) {
      setLocationError(error?.message || 'Live locations could not be loaded');
    } finally {
      if (!silent) setLoadingLiveData(false);
    }
  }, [incident?.id]);

  const refreshDirectory = useCallback(async () => {
    if (!incident?.id) return;
    try {
      const response = await CommanServices.getIncidentRecipientDirectory(incident.id);
      if (response?.status === false) {
        throw new Error(response?.message || response?.detail || 'Responder directory could not be loaded');
      }
      const directory = normalizeIncidentRecipientDirectory(response);
      setUsers(directory.users);
      setAuthoritativeInvolvedUserIds(directory.involvedUserIds);
      setDirectoryError('');
    } catch (error) {
      setDirectoryError(error?.message || 'Responder directory could not be loaded');
      throw error;
    }
  }, [incident?.id]);

  const refreshAll = useCallback(async ({ silent = false } = {}) => {
    if (!incident?.id) return;
    if (!silent) setLoadingLiveData(true);
    try {
      await Promise.all([
        refreshLocations({ silent: true }),
        refreshDirectory(),
      ]);
    } catch (_) {
      // Each source keeps its own error so a successful socket location update
      // cannot hide a directory/authorization failure (and vice versa).
    } finally {
      if (!silent) setLoadingLiveData(false);
    }
  }, [incident?.id, refreshDirectory, refreshLocations]);

  useEffect(() => {
    if (!incident?.id) return undefined;
    let mounted = true;
    refreshAll();

    const mergeSocketLocation = (event, statusOverride = null) => {
      if (!mounted) return;
      const userId = String(event?.user_id || '').trim();
      if (!userId || event?.is_admin_team_marker) return;
      const socketRow = {
        ...event,
        user_id: userId,
        status: statusOverride || event?.status,
        timestamp: event?.timestamp || event?.updated_at || new Date().toISOString(),
      };
      setLocationHistory((previous) => mergeGeoFenceLocationRows(previous, [socketRow]));
      setFreshnessNow(Date.now());
      setLocationError('');
    };
    const unsubscribeLocation = socketService.on('team_location_updated', (event) => {
      mergeSocketLocation(event);
    });
    const unsubscribeOffline = socketService.on('team_location_offline', (event) => {
      mergeSocketLocation(event, 'offline');
    });
    const unsubscribeReconnect = socketService.on('connect', () => refreshAll({ silent: true }));
    const pollTimer = window.setInterval(() => refreshLocations({ silent: true }), 15_000);
    const freshnessTimer = window.setInterval(() => setFreshnessNow(Date.now()), 30_000);

    return () => {
      mounted = false;
      unsubscribeLocation?.();
      unsubscribeOffline?.();
      unsubscribeReconnect?.();
      window.clearInterval(pollTimer);
      window.clearInterval(freshnessTimer);
    };
  }, [incident?.id, refreshAll, refreshLocations]);

  const defaultCenter = useMemo(() => {
    const gps = incident?.gps_coordinates;
    if (gps?.latitude != null) return { lat: Number(gps.latitude), lng: Number(gps.longitude) };
    if (locations.length) return { lat: Number(locations[0].lat), lng: Number(locations[0].lng) };
    return { lat: 24.7136, lng: 46.6753 };
  }, [incident, locations]);

  useEffect(() => {
    if (center || !mapRef.current || !locations.length || !window.google?.maps?.LatLngBounds) return;
    if (locations.length === 1) {
      mapRef.current.setCenter({ lat: Number(locations[0].lat), lng: Number(locations[0].lng) });
      mapRef.current.setZoom(13);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    locations.forEach((location) => bounds.extend({
      lat: Number(location.lat),
      lng: Number(location.lng),
    }));
    mapRef.current.fitBounds(bounds, 56);
  }, [center, locations]);

  const inside = useMemo(() => {
    if (!center) return [];
    return locations
      .filter((l) => distKm(center, { lat: Number(l.lat), lng: Number(l.lng) }) <= radiusKm)
      .map((l) => {
        const u = users.find((x) => userId(x) === String(l.user_id));
        return { userId: String(l.user_id), name: u ? userLabel(u) : String(l.user_id).slice(0, 8) };
      });
  }, [center, radiusKm, locations, users]);

  const targetSignature = useMemo(() => [
    center?.lat ?? '',
    center?.lng ?? '',
    radiusKm,
    ...inside.map((user) => String(user.userId)).sort(),
  ].join('|'), [center, inside, radiusKm]);

  useEffect(() => {
    actionDispatchIdsRef.current = {};
  }, [targetSignature]);

  const act = async (type) => {
    if (!inside.length || sending) return;
    const dispatchId = getOrCreateGeoFenceDispatchId(actionDispatchIdsRef.current, type);
    setSending(type);
    try {
      const completed = await onAction(
        type,
        inside.map((u) => u.userId),
        inside.map((u) => u.name),
        { center, radiusKm, dispatchId }
      );
      if (completed !== false) delete actionDispatchIdsRef.current[type];
    } catch (error) {
      // The caller owns user-facing delivery errors. Keep the dispatch id so a
      // retry is idempotent rather than creating a duplicate timeline card.
      console.error('Geo-fence action failed:', error);
    } finally {
      setSending(null);
    }
  };

  const ACTIONS = [
    { id: 'notification', label: 'Notification', icon: Bell, cls: 'bg-amber-600 hover:bg-amber-700' },
    { id: 'verification', label: 'Verification', icon: CheckCircle2, cls: 'bg-emerald-600 hover:bg-emerald-700' },
    { id: 'status', label: 'Status Update', icon: MessageSquare, cls: 'bg-sky-600 hover:bg-sky-700' },
    { id: 'action_card', label: 'Action Card', icon: ClipboardList, cls: 'bg-indigo-600 hover:bg-indigo-700' },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4"
      data-testid="geofence-modal"
      data-modal-overlay="true"
      role="dialog"
      aria-modal="true"
      aria-labelledby="geofence-modal-title"
    >
      <div className="bg-white rounded-xl shadow-2xl w-[760px] max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 id="geofence-modal-title" className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Target className="h-4 w-4 text-indigo-600" /> Geo-Fence Targeting
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Click the map to place the circle — green responders are live and gray responders show their last known position.
            </p>
          </div>
          <button onClick={onClose} data-testid="geofence-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        <div className="no-scrollbar p-4 space-y-3 overflow-y-auto">
          {/* User pool — whose positions count */}
          <div className="flex flex-wrap items-center gap-2" data-testid="geofence-pool">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0">Target pool</span>
            <button
              type="button"
              onClick={() => setPool('all')}
              data-testid="geofence-pool-all"
              className={`rounded-full border px-3 py-1 text-[11px] font-bold transition-colors ${pool === 'all' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              {allowedSet ? 'My incident scope' : 'All app users'} ({mappableLocations.length} mapped · {scopedUsers.length} total)
            </button>
            <button
              type="button"
              onClick={() => setPool('involved')}
              data-testid="geofence-pool-involved"
              title="Only users already involved in this incident (assigned, acknowledged, participating)"
              className={`rounded-full border px-3 py-1 text-[11px] font-bold transition-colors ${pool === 'involved' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Involved in this incident ({involvedMappedCount} mapped · {involvedUserCount} total)
            </button>
          </div>
          <p className="text-[11px] text-slate-500" data-testid="geofence-freshness-note">
            Green = updated within the last {Math.round(GEO_FENCE_LOCATION_FRESHNESS_MS / 60000)} minutes. Gray = last known position. Both can be included in the circle and notified.
            {unmappedUserCount > 0 ? ` ${unmappedUserCount} user${unmappedUserCount === 1 ? ' has' : 's have'} not shared a valid GPS position yet.` : ''}
          </p>
          {liveDataError && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert" data-testid="geofence-live-error">
              <span className="flex min-w-0 items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="truncate">{liveDataError}</span>
              </span>
              <button type="button" onClick={() => refreshAll()} className="shrink-0 font-bold underline underline-offset-2">Retry</button>
            </div>
          )}

          {/* Map */}
          <div className="h-72 rounded-lg overflow-hidden border border-gray-200">
            {mapsUnavailable ? (
              <MapUnavailable center={defaultCenter} label="Map unavailable — geo-fencing needs the map" />
            ) : !isLoaded || (loadingLiveData && locations.length === 0) ? (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Loading responder positions…</div>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center || defaultCenter}
                zoom={center ? 12 : 10}
                onLoad={(map) => { mapRef.current = map; }}
                onUnmount={() => { mapRef.current = null; }}
                onClick={(e) => e.latLng && setCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
                options={{ streetViewControl: false, mapTypeControl: false, draggableCursor: 'crosshair' }}
              >
                {locations.map((l) => {
                  const u = users.find((x) => userId(x) === String(l.user_id));
                  const isIn = center && distKm(center, { lat: Number(l.lat), lng: Number(l.lng) }) <= radiusKm;
                  const locationState = geoFenceLocationState(l, freshnessNow);
                  const markerColor = locationState.live ? '#10b981' : '#94a3b8';
                  return (
                    <Marker
                      key={l.user_id}
                      position={{ lat: Number(l.lat), lng: Number(l.lng) }}
                      title={`${u ? userLabel(u) : String(l.user_id)} · ${locationState.live ? 'Live now' : 'Last known position'}`}
                      icon={window.google ? {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: markerColor,
                        fillOpacity: 1,
                        strokeColor: isIn ? '#1d4ed8' : '#ffffff',
                        strokeWeight: isIn ? 4 : 2,
                        scale: isIn ? 9 : 7,
                      } : undefined}
                    />
                  );
                })}
                {center && (
                  <Circle
                    center={center}
                    radius={radiusKm * 1000}
                    options={{ fillColor: '#4f46e5', fillOpacity: 0.12, strokeColor: '#4f46e5', strokeWeight: 2, clickable: false }}
                  />
                )}
              </GoogleMap>
            )}
          </div>

          {/* Radius + count */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0">Radius</span>
            <input
              type="range" min={0.2} max={50} step={0.2}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              data-testid="geofence-radius"
              className="flex-1 accent-indigo-600"
            />
            <span className="text-sm font-black text-indigo-600 w-20 text-right">{radiusKm.toFixed(1)} km</span>
            <span
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold flex-shrink-0 ${inside.length ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}
              data-testid="geofence-count"
            >
              <Users className="h-3.5 w-3.5" /> {center ? `${inside.length} user${inside.length === 1 ? '' : 's'} inside` : 'Click the map to place the circle'}
            </span>
          </div>

          {/* Users inside */}
          {inside.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="geofence-users">
              {inside.map((u) => (
                <span key={u.userId} className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs rounded-full px-2 py-1">
                  {u.name}
                </span>
              ))}
            </div>
          )}
          {center && inside.length === 0 && (
            <p className="text-xs text-gray-400">No mapped users inside this circle — move it or widen the radius.</p>
          )}

          {/* Actions */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Send to everyone inside</p>
            <div className="grid grid-cols-4 gap-2">
              {ACTIONS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  disabled={!inside.length || !!sending}
                  onClick={() => act(a.id)}
                  data-testid={`geofence-action-${a.id}`}
                  className={`flex flex-col items-center gap-1 rounded-lg text-white text-[11px] font-bold px-2 py-2.5 disabled:opacity-40 transition-colors ${a.cls}`}
                >
                  {sending === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <a.icon className="h-4 w-4" />}
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GeoFenceModal;
